import React, { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Icon } from "../components/Icons";
import { useAuth } from "../context/AuthContext";
import ClientManagerTimesheet from "../components/ClientManagerTimesheet";
import dayjs from "dayjs";
import { v4 as uuidv4 } from "uuid";
import "../styles/ClientManagerDashboard.css";

const token = () => sessionStorage.getItem("jwtToken");
const hdr   = () => ({ Authorization: `Bearer ${token()}` });
const fmt   = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
const getGreeting = () => { const h=new Date().getHours(); return h<12?"Good Morning":h<17?"Good Afternoon":"Good Evening"; };

const getSunSat = (base=new Date()) => {
  const d=new Date(base); d.setHours(0,0,0,0);
  const sun=new Date(d); sun.setDate(d.getDate()-d.getDay());
  const sat=new Date(sun); sat.setDate(sun.getDate()+6);
  return { weekStart:fmt(sun), weekEnd:fmt(sat) };
};
const getCurrentSunday = () => {
  const today=new Date(); today.setHours(0,0,0,0);
  const curr=new Date(today); curr.setDate(today.getDate()-today.getDay());
  return fmt(curr);
};

const NAV = [
  { key:"dashboard",   label:"Dashboard",            icon:"Home" },
  { key:"approvals",   label:"Timesheet Management", icon:"CheckCircle" },
  { key:"employees",   label:"Employees",             icon:"Users" },
  { key:"reports",     label:"Reports",               icon:"BarChart" },
  { key:"feedback",    label:"Feedback",              icon:"MessageSquare" },
  { key:"settings",    label:"Settings",              icon:"Badge" },
];

const STATUS_TABS = ["Pending","Approved","Rejected"];

/* Circular progress */
const CircProg = ({ pct, size=80, stroke=7 }) => {
  const r=Math.floor((size-stroke*2)/2), cx=size/2, cy=size/2;
  const circ=2*Math.PI*r, dash=(pct/100)*circ;
  return (
    <div className="cm-progress-circle" style={{width:size,height:size}}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#F1F5F9" strokeWidth={stroke}/>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#2563EB" strokeWidth={stroke}
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"/>
      </svg>
      <div className="cm-progress-circle-text">{pct}%</div>
    </div>
  );
};

/* ── ClientFeedbackView ── */
const ClientFeedbackView = ({ username, name }) => {
  const [strengths, setStrengths] = useState("");
  const [gaps, setGaps] = useState("");
  const [suggestions, setSuggestions] = useState("");
  const [loading, setLoading] = useState(false);
  const [feedbackList, setFeedbackList] = useState([]);

  const [editingId, setEditingId] = useState(null);
  const [editStrengths, setEditStrengths] = useState("");
  const [editGaps, setEditGaps] = useState("");
  const [editSuggestions, setEditSuggestions] = useState("");
  
  const currentMonthStr = dayjs().format("MMMM YYYY"); // e.g. "August 2026"
  const storageKey = `workforcemgt_client_feedback_${username}`;

  // Load history from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        setFeedbackList(JSON.parse(stored));
      }
    } catch (e) {
      console.error("Error loading feedback history", e);
    }
  }, [storageKey]);

  // Check if current month is already submitted
  const currentMonthFeedback = feedbackList.find(f => f.month === currentMonthStr);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!strengths.trim() || !gaps.trim() || !suggestions.trim()) return;
    
    setLoading(true);
    setTimeout(() => {
      const newFeedback = {
        id: uuidv4(),
        month: currentMonthStr,
        strengths: strengths.trim(),
        gaps: gaps.trim(),
        suggestions: suggestions.trim(),
        dateSubmitted: dayjs().format("MMM D, YYYY h:mm A")
      };
      
      const updatedList = [newFeedback, ...feedbackList];
      setFeedbackList(updatedList);
      localStorage.setItem(storageKey, JSON.stringify(updatedList));
      
      setStrengths("");
      setGaps("");
      setSuggestions("");
      setLoading(false);
    }, 800);
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditStrengths(item.strengths || "");
    setEditGaps(item.gaps || "");
    setEditSuggestions(item.suggestions || "");
  };

  const handleSaveEdit = (id) => {
    if (!editStrengths.trim() || !editGaps.trim() || !editSuggestions.trim()) return;
    const updatedList = feedbackList.map(f => {
      if (f.id === id) {
        return {
          ...f,
          strengths: editStrengths.trim(),
          gaps: editGaps.trim(),
          suggestions: editSuggestions.trim(),
          modified: true,
          dateModified: dayjs().format("MMM D, YYYY h:mm A")
        };
      }
      return f;
    });
    setFeedbackList(updatedList);
    localStorage.setItem(storageKey, JSON.stringify(updatedList));
    setEditingId(null);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, width: "100%", textAlign: "left" }}>
      {/* Retrospective Form / Current Submission Card */}
      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 16, padding: "32px", boxShadow: "0 4px 20px rgba(0,0,0,0.02)", display: "flex", flexDirection: "column", gap: 24, position: "relative" }}>
        <div style={{ position: "absolute", right: 32, top: 20, pointerEvents: "none", zIndex: 10 }}>
          {/* Inline SVG illustration */}
          <svg width="160" height="135" viewBox="0 0 160 135" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Background soft blob */}
            <path d="M-5 65C-5 35 15 15 45 15C75 15 95 30 95 60C95 90 70 110 40 110C10 110 -5 95 -5 65Z" fill="#EFF6FF" opacity="0.9"/>
            {/* Floating sparkles */}
            <path d="M5 25 L6.5 28.5 L10 30 L6.5 31.5 L5 35 L3.5 31.5 L0 30 L3.5 28.5 Z" fill="#93C5FD" opacity="0.6"/>
            <path d="M90 40 L91.5 43.5 L95 45 L91.5 46.5 L90 50 L88.5 46.5 L85 45 L88.5 43.5 Z" fill="#93C5FD" opacity="0.5"/>
            <path d="M95 85 L96.5 88.5 L100 90 L96.5 91.5 L95 95 L93.5 91.5 L90 90 L93.5 88.5 Z" fill="#93C5FD" opacity="0.4"/>
            
            {/* Clipboard Document */}
            <rect x="12" y="32" width="66" height="78" rx="8" fill="#FFFFFF" stroke="#D1E2FF" strokeWidth="2.5"/>
            
            {/* Clipboard top clip */}
            <rect x="32" y="27" width="26" height="8" rx="3" fill="#BACFFD"/>
            
            {/* Lines on clipboard */}
            <rect x="22" y="48" width="46" height="3" rx="1.5" fill="#E2EEFF"/>
            <rect x="22" y="58" width="46" height="3" rx="1.5" fill="#E2EEFF"/>
            <rect x="22" y="68" width="28" height="3" rx="1.5" fill="#E2EEFF"/>
            
            {/* Blue Pencil pointing down-left */}
            <g transform="translate(88, 72) rotate(-40)">
              {/* Eraser */}
              <rect x="-6" y="-35" width="12" height="5" fill="#93C5FD" rx="1"/>
              {/* Body */}
              <rect x="-6" y="-30" width="12" height="25" fill="#2563EB"/>
              {/* Tip */}
              <polygon points="-6,-5 0,5 6,-5" fill="#E2EEFF"/>
              {/* Lead */}
              <polygon points="-2.5,-1 0,5 2.5,-1" fill="#2563EB"/>
            </g>
            
            {/* Speech Bubble */}
            <path d="M40 22C40 11.5 49 3 60 3H88C99 3 108 11.5 108 22C108 32.5 99 41 88 41H78L67 49V41H60C49 41 40 32.5 40 22Z" fill="#2563EB"/>
            {/* Speech bubble lines */}
            <rect x="54" y="15" width="40" height="3" rx="1.5" fill="#FFFFFF"/>
            <rect x="54" y="24" width="28" height="3" rx="1.5" fill="#FFFFFF"/>
          </svg>
        </div>
        {currentMonthFeedback ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#ECFDF5", border: "1px solid #A7F3D0", padding: "8px 12px", borderRadius: 8, marginRight: 180 }}>
              <Icon.CheckCircle size={16} color="#10B981" style={{ flexShrink: 0 }} />
              <div style={{ fontSize: "0.8rem", color: "#065F46", fontWeight: 600 }}>
                You have already submitted your feedback for {currentMonthStr}. Thank you!
              </div>
            </div>
            
            <div style={{ borderTop: "1px solid #EFF6FF", paddingTop: 8, display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <div style={{ flex: 1, paddingRight: 180 }}>
                  <h4 style={{ fontSize: "0.95rem", fontWeight: 700, color: "#0F172A", margin: 0 }}>Your {currentMonthStr} Feedback</h4>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "2px 0 6px" }}>
                    <span style={{ fontSize: "0.72rem", color: "#94A3B8" }}>
                      {currentMonthFeedback.dateModified ? `Modified: ${currentMonthFeedback.dateModified}` : `Submitted: ${currentMonthFeedback.dateSubmitted}`}
                    </span>
                    {currentMonthFeedback.modified && (
                      <span style={{ fontSize: "0.65rem", fontWeight: 600, background: "#F1F5F9", color: "#475569", padding: "1px 5px", borderRadius: 4 }}>
                        ✍️ Modified
                      </span>
                    )}
                  </div>
                  {!currentMonthFeedback.modified && editingId !== currentMonthFeedback.id && (
                    <button 
                      onClick={() => startEdit(currentMonthFeedback)}
                      style={{ background: "none", color: "#2563EB", cursor: "pointer", fontSize: "0.72rem", fontWeight: 600, padding: "4px 8px", border: "1px solid #E2D8F0", borderRadius: 6, display: "inline-flex", alignItems: "center", gap: 4 }}
                      title="Modify Feedback"
                    >
                      ✏️ Modify
                    </button>
                  )}
                </div>
              </div>
              
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {[
                  { 
                    title: "Strengths: What went well regarding employee performance, delivery, or collaboration this month?", 
                    value: currentMonthFeedback.strengths,
                    color: "#10B981",
                    icon: (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                      </svg>
                    )
                  },
                  { 
                    title: "Collaboration Gaps: What can we improve to make your engagement and experience smoother?", 
                    value: currentMonthFeedback.gaps,
                    color: "#EF4444",
                    icon: (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                        <line x1="12" y1="9" x2="12" y2="13"/>
                        <line x1="12" y1="17" x2="12.01" y2="17"/>
                      </svg>
                    )
                  },
                  { 
                    title: "Key Suggestions: What specific improvements or suggestions do you have for our team?", 
                    value: currentMonthFeedback.suggestions,
                    color: "#F59E0B",
                    icon: (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A5 5 0 0 0 8 8c0 1 .3 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/>
                        <line x1="9" y1="18" x2="15" y2="18"/>
                        <line x1="10" y1="22" x2="14" y2="22"/>
                      </svg>
                    )
                  }
                ].map(item => (
                  <div key={item.title} style={{ display: "flex", gap: 18, border: "1px solid #E2E8F0", borderRadius: 12, padding: "20px 24px", background: "#fff", alignItems: "flex-start" }}>
                    <div style={{ width: 42, height: 42, borderRadius: "50%", background: item.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {item.icon}
                    </div>
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
                      <span style={{ fontSize: "0.85rem", fontWeight: 700, color: "#0F172A", lineHeight: 1.3 }}>{item.title}</span>
                      <p style={{ fontSize: "0.8125rem", fontWeight: 400, color: "#475569", margin: 0, whiteSpace: "pre-line", lineHeight: 1.4 }}>{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            {/* Header with Title, Subtitle */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 20 }}>
              <div style={{ flex: 1, paddingRight: 180 }}>
                <h4 style={{ fontSize: "1.1rem", fontWeight: 700, color: "#0F172A", margin: "0 0 8px" }}>
                  Monthly Engagement Feedback ({currentMonthStr})
                </h4>
                <p style={{ fontSize: "0.8rem", color: "#475569", margin: 0, lineHeight: 1.5 }}>
                  Provide monthly feedback regarding your collaboration with the team. Tell us what is going well, where we can improve, and how we can elevate your experience.
                </p>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Question 1 */}
              <div style={{ 
                display: "flex", 
                gap: 14, 
                border: "1px solid #E2E8F0", 
                borderRadius: 12, 
                padding: "16px 20px", 
                background: "#fff",
                alignItems: "flex-start"
              }}>
                <div style={{
                  width: 38,
                  height: 38,
                  borderRadius: "50%",
                  background: "#10B981",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0
                }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                  </svg>
                </div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                  <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#0F172A", lineHeight: 1.3 }}>
                    Strengths: What went well regarding employee performance, delivery, or collaboration this month?
                  </label>
                  <textarea
                    rows="2"
                    value={strengths}
                    onChange={(e) => setStrengths(e.target.value)}
                    placeholder="E.g., high quality of work, professional communication, timely updates, or specific employees who went above and beyond..."
                    required
                    style={{ 
                      width: "100%",
                      height: "54px",
                      padding: "8px 12px", 
                      background: "#fff", 
                      border: "1px solid #CBD5E1", 
                      borderRadius: 6, 
                      fontSize: "0.8125rem", 
                      fontWeight: 400, 
                      color: "#0F172A", 
                      outline: "none", 
                      resize: "vertical", 
                      fontFamily: "inherit",
                      lineHeight: 1.4,
                      boxSizing: "border-box",
                      transition: "border-color 0.2s, box-shadow 0.2s"
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#2563EB";
                      e.target.style.boxShadow = "0 0 0 3px rgba(37, 99, 235, 0.1)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#CBD5E1";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                </div>
              </div>

              {/* Question 2 */}
              <div style={{ 
                display: "flex", 
                gap: 14, 
                border: "1px solid #E2E8F0", 
                borderRadius: 12, 
                padding: "16px 20px", 
                background: "#fff",
                alignItems: "flex-start"
              }}>
                <div style={{
                  width: 38,
                  height: 38,
                  borderRadius: "50%",
                  background: "#EF4444",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0
                }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/>
                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                </div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                  <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#0F172A", lineHeight: 1.3 }}>
                    Collaboration Gaps: What can we improve to make your engagement and experience smoother?
                  </label>
                  <textarea
                    rows="2"
                    value={gaps}
                    onChange={(e) => setGaps(e.target.value)}
                    placeholder="E.g., response times, clarity in updates, onboarding support, or alignment on expectations..."
                    required
                    style={{ 
                      width: "100%",
                      height: "54px",
                      padding: "8px 12px", 
                      background: "#fff", 
                      border: "1px solid #CBD5E1", 
                      borderRadius: 6, 
                      fontSize: "0.8125rem", 
                      fontWeight: 400, 
                      color: "#0F172A", 
                      outline: "none", 
                      resize: "vertical", 
                      fontFamily: "inherit",
                      lineHeight: 1.4,
                      boxSizing: "border-box",
                      transition: "border-color 0.2s, box-shadow 0.2s"
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#2563EB";
                      e.target.style.boxShadow = "0 0 0 3px rgba(37, 99, 235, 0.1)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#CBD5E1";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                </div>
              </div>

              {/* Question 3 */}
              <div style={{ 
                display: "flex", 
                gap: 14, 
                border: "1px solid #E2E8F0", 
                borderRadius: 12, 
                padding: "16px 20px", 
                background: "#fff",
                alignItems: "flex-start"
              }}>
                <div style={{
                  width: 38,
                  height: 38,
                  borderRadius: "50%",
                  background: "#F59E0B",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0
                }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A5 5 0 0 0 8 8c0 1 .3 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/>
                    <line x1="9" y1="18" x2="15" y2="18"/>
                    <line x1="10" y1="22" x2="14" y2="22"/>
                  </svg>
                </div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                  <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#0F172A", lineHeight: 1.3 }}>
                    Key Suggestions: What specific improvements or suggestions do you have for our team?
                  </label>
                  <textarea
                    rows="2"
                    value={suggestions}
                    onChange={(e) => setSuggestions(e.target.value)}
                    placeholder="E.g., new processes, training suggestions, feedback on tools, or ideas to elevate service quality..."
                    required
                    style={{ 
                      width: "100%",
                      height: "54px",
                      padding: "8px 12px", 
                      background: "#fff", 
                      border: "1px solid #CBD5E1", 
                      borderRadius: 6, 
                      fontSize: "0.8125rem", 
                      fontWeight: 400, 
                      color: "#0F172A", 
                      outline: "none", 
                      resize: "vertical", 
                      fontFamily: "inherit",
                      lineHeight: 1.4,
                      boxSizing: "border-box",
                      transition: "border-color 0.2s, box-shadow 0.2s"
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#2563EB";
                      e.target.style.boxShadow = "0 0 0 3px rgba(37, 99, 235, 0.1)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#CBD5E1";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !strengths.trim() || !gaps.trim() || !suggestions.trim()}
              style={{ 
                display: "inline-flex", 
                alignItems: "center", 
                justifyContent: "center", 
                gap: 8, 
                padding: "12px 24px", 
                background: loading || !strengths.trim() || !gaps.trim() || !suggestions.trim() ? "#94A3B8" : "#2563EB", 
                color: "#fff", 
                border: "none", 
                borderRadius: 8, 
                fontSize: "0.875rem", 
                fontWeight: 600, 
                cursor: loading || !strengths.trim() || !gaps.trim() || !suggestions.trim() ? "not-allowed" : "pointer", 
                transition: "background 0.2s, transform 0.1s",
                alignSelf: "flex-start" 
              }}
              onMouseEnter={(e) => {
                if (!loading && strengths.trim() && gaps.trim() && suggestions.trim()) {
                  e.target.style.background = "#1D4ED8";
                }
              }}
              onMouseLeave={(e) => {
                if (!loading && strengths.trim() && gaps.trim() && suggestions.trim()) {
                  e.target.style.background = "#2563EB";
                }
              }}
            >
              {loading ? "Submitting..." : "Submit Monthly Feedback"}
              {!loading && <Icon.ArrowRight size={16} color="#fff" />}
            </button>
          </form>
        )}
      </div>

      {/* Feedback History Card */}
      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 16, padding: "32px", boxShadow: "0 4px 20px rgba(0,0,0,0.02)", display: "flex", flexDirection: "column", gap: 24 }}>
        <h4 style={{ fontSize: "1.1rem", fontWeight: 700, color: "#0F172A", margin: 0 }}>Feedback History</h4>
        
        {feedbackList.length === 0 ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "40px 10px", color: "#94A3B8", fontSize: "0.875rem", textAlign: "center", border: "1px dashed #E2E8F0", borderRadius: 12 }}>
            <Icon.FileText size={32} color="#CBD5E1" />
            <p style={{ margin: 0, color: "#64748B" }}>No feedback submitted yet. Your submissions will be saved here.</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {feedbackList.map((item) => {
              const isEditing = editingId === item.id;
              return (
                <div key={item.id} style={{ border: "1px solid #E2E8F0", borderRadius: 12, padding: "20px", background: "#fff", display: "flex", flexDirection: "column", gap: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #F1F5F9", paddingBottom: 12, margin: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: "0.95rem", fontWeight: 700, color: "#0F172A" }}>{item.month}</span>
                      {item.modified && (
                        <span style={{ display: "inline-flex", alignItems: "center", fontSize: "0.7rem", fontWeight: 600, background: "#F1F5F9", color: "#475569", padding: "3px 8px", borderRadius: 999, border: "1px solid #E2E8F0" }}>
                          Modified
                        </span>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{ fontSize: "0.75rem", color: "#94A3B8" }}>
                        {item.dateModified ? `Modified: ${item.dateModified}` : `Submitted: ${item.dateSubmitted}`}
                      </span>
                      {!isEditing && !item.modified && (
                        <button 
                          onClick={() => startEdit(item)}
                          style={{ background: "none", border: "1px solid #E2E8F0", color: "#2563EB", cursor: "pointer", fontSize: "0.75rem", fontWeight: 600, padding: "5px 10px", borderRadius: 6, display: "inline-flex", alignItems: "center", gap: 4, transition: "all 0.2s" }}
                          onMouseEnter={(e) => { e.target.style.borderColor = "#2563EB"; e.target.style.background = "#EFF6FF"; }}
                          onMouseLeave={(e) => { e.target.style.borderColor = "#E2E8F0"; e.target.style.background = "none"; }}
                          title="Modify Feedback"
                        >
                          <Icon.Edit size={12} color="#2563EB" /> Modify
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {isEditing ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <label style={{ fontSize: "0.8rem", fontWeight: 700, color: "#10B981", textTransform: "uppercase", letterSpacing: "0.04em" }}>🟢 Strengths:</label>
                        <textarea
                          rows="2"
                          value={editStrengths}
                          onChange={(e) => setEditStrengths(e.target.value)}
                          style={{ padding: "10px 14px", border: "1.5px solid #CBD5E1", borderRadius: 8, fontSize: "0.875rem", color: "#0F172A", outline: "none", resize: "vertical", fontFamily: "inherit", lineHeight: 1.5, boxSizing: "border-box" }}
                        />
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <label style={{ fontSize: "0.8rem", fontWeight: 700, color: "#EF4444", textTransform: "uppercase", letterSpacing: "0.04em" }}>🔴 Collaboration Gaps:</label>
                        <textarea
                          rows="2"
                          value={editGaps}
                          onChange={(e) => setEditGaps(e.target.value)}
                          style={{ padding: "10px 14px", border: "1.5px solid #CBD5E1", borderRadius: 8, fontSize: "0.875rem", color: "#0F172A", outline: "none", resize: "vertical", fontFamily: "inherit", lineHeight: 1.5, boxSizing: "border-box" }}
                        />
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <label style={{ fontSize: "0.8rem", fontWeight: 700, color: "#F59E0B", textTransform: "uppercase", letterSpacing: "0.04em" }}>💡 Key Suggestions:</label>
                        <textarea
                          rows="2"
                          value={editSuggestions}
                          onChange={(e) => setEditSuggestions(e.target.value)}
                          style={{ padding: "10px 14px", border: "1.5px solid #CBD5E1", borderRadius: 8, fontSize: "0.875rem", color: "#0F172A", outline: "none", resize: "vertical", fontFamily: "inherit", lineHeight: 1.5, boxSizing: "border-box" }}
                        />
                      </div>
                      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                        <button
                          onClick={() => handleSaveEdit(item.id)}
                          disabled={!editStrengths.trim() || !editGaps.trim() || !editSuggestions.trim()}
                          style={{ padding: "8px 18px", background: "#2563EB", color: "#fff", border: "none", borderRadius: 8, fontSize: "0.8rem", fontWeight: 600, cursor: "pointer", transition: "background 0.2s" }}
                          onMouseEnter={(e) => { e.target.style.background = "#1D4ED8"; }}
                          onMouseLeave={(e) => { e.target.style.background = "#2563EB"; }}
                        >
                          Save Changes
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          style={{ padding: "8px 18px", background: "#F1F5F9", color: "#475569", border: "none", borderRadius: 8, fontSize: "0.8rem", fontWeight: 600, cursor: "pointer" }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                      {/* Highlights */}
                      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                        <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#10B981", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                          </svg>
                        </div>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748B" }}>Strengths & Highlights</span>
                          <p style={{ fontSize: "0.875rem", color: "#334155", margin: "4px 0 0", lineHeight: 1.5, background: "#F8FAFC", padding: "10px 14px", borderRadius: 8, border: "1px solid #F1F5F9" }}>{item.strengths}</p>
                        </div>
                      </div>
                      
                      {/* Challenges */}
                      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                        <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#EF4444", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                            <line x1="12" y1="9" x2="12" y2="13" />
                            <line x1="12" y1="17" x2="12.01" y2="17" />
                          </svg>
                        </div>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748B" }}>Collaboration Gaps</span>
                          <p style={{ fontSize: "0.875rem", color: "#334155", margin: "4px 0 0", lineHeight: 1.5, background: "#F8FAFC", padding: "10px 14px", borderRadius: 8, border: "1px solid #F1F5F9" }}>{item.gaps}</p>
                        </div>
                      </div>
                      
                      {/* Actionable Ideas */}
                      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                        <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#F59E0B", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A5 5 0 0 0 8 8c0 1 .3 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
                            <line x1="9" y1="18" x2="15" y2="18" />
                            <line x1="10" y1="22" x2="14" y2="22" />
                          </svg>
                        </div>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748B" }}>Key Suggestions & Improvements</span>
                          <p style={{ fontSize: "0.875rem", color: "#334155", margin: "4px 0 0", lineHeight: 1.5, background: "#F8FAFC", padding: "10px 14px", borderRadius: 8, border: "1px solid #F1F5F9" }}>{item.suggestions}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const ClientManagerHome = () => {
  const { username, name, isAuthenticated } = useAuth();

  /* ── data ── */
  const [employees,   setEmployees]   = useState([]);
  const [rows,        setRows]        = useState([]); // { employee, timesheet|null }
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState("");
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  /* ── week picker ── */
  const [selectedDate] = useState(getCurrentSunday);
  const { weekStart, weekEnd } = useMemo(() => getSunSat(new Date(selectedDate+"T00:00:00")), [selectedDate]);

  /* ── approvals date range picker ── */
  const [approvalFromDate, setApprovalFromDate] = useState("");
  const [approvalToDate, setApprovalToDate] = useState("");

  /* ── ui ── */
  const [activeNav, setActiveNav] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get("tab") || params.get("nav");
      if (tab && NAV.some(n => n.key === tab.toLowerCase())) {
        return tab.toLowerCase();
      }
    } catch (e) {
      // ignore
    }
    return "dashboard";
  });

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get("tab") || params.get("nav");
      if (tab && NAV.some(n => n.key === tab.toLowerCase())) {
        setActiveNav(tab.toLowerCase());
      }
    } catch (e) {
      // ignore
    }
  }, []);
  const [activeTab,   setActiveTab]   = useState("Pending");
  const [searchQuery, setSearchQuery] = useState("");
  const [selected,    setSelected]    = useState(new Set());
  const [expandedId,  setExpandedId]  = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [refreshKey,  setRefreshKey]  = useState(0);
  const [updateMsg,   setUpdateMsg]   = useState("");
  const [showSupport, setShowSupport] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [settingsEmail, setSettingsEmail] = useState("");
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [savingDob, setSavingDob] = useState(false);
  const [hasExistingDob, setHasExistingDob] = useState(false);
  const [dobMsg, setDobMsg] = useState(null);
  const PAGE_SIZE = 10;

  useEffect(() => { setCurrentPage(1); }, [searchQuery, activeTab]);

  // Fetch user profile
  useEffect(() => {
    if (!username) return;
    (async () => {
      try {
        const res = await fetch(`/timesheets/users/${username}/profile`, { headers: hdr() });
        if (res.ok) {
          const data = await res.json();
          setProfileData(data);
          if (data.email) setSettingsEmail(data.email);
          if (data.dateOfBirth) {
            setDateOfBirth(data.dateOfBirth);
            setHasExistingDob(true);
          }
        }
      } catch (e) { console.error(e); }
    })();
  }, [username]);

  const handleSaveDob = async () => {
    if (!dateOfBirth) return;
    setSavingDob(true);
    setDobMsg(null);
    try {
      const res = await fetch(`/timesheets/users/${username}/dateOfBirth`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...hdr()
        },
        body: JSON.stringify({ dateOfBirth })
      });
      if (res.ok) {
        setHasExistingDob(true);
        setProfileData(prev => ({ ...prev, dateOfBirth }));
        setDobMsg({ type: "success", text: "Date of Birth saved successfully!" });
      } else {
        setDobMsg({ type: "error", text: "Failed to save Date of Birth." });
      }
    } catch (e) {
      console.error(e);
      setDobMsg({ type: "error", text: "Failed to save Date of Birth." });
    } finally {
      setSavingDob(false);
    }
  };

  /* ── fetch employees ── */
  useEffect(() => {
    if (!username || !isAuthenticated) return;
    (async () => {
      try {
        const res = await fetch(`/timesheets/getUsers/${username}`, { headers: hdr() });
        if (res.ok) {
          const data = await res.json();
          setEmployees((data?.usersResponseList||[]).map(u => ({
            fullName: u.fullName, username: u.userName, active: u.active, startDate: u.startDate,
          })));
        }
      } catch(e) { console.error(e); }
    })();
  }, [username, isAuthenticated]);

  /* ── all timesheets (1-year range) for Approvals + Employees views ── */
  const [allRows, setAllRows] = useState([]); // { employee, timesheet }[]
  const [allLoading, setAllLoading] = useState(false);

  useEffect(() => {
    if (!employees.length) return;
    setAllLoading(true);
    const today = new Date();
    const oneYearAgo = new Date(); oneYearAgo.setFullYear(today.getFullYear() - 1);
    const ws = fmt(oneYearAgo), we = fmt(today);
    (async () => {
      try {
        const results = await Promise.all(employees.map(async emp => {
          const res = await fetch(`/timesheets/operations/byDates/${emp.username}?weekStart=${ws}&weekEnd=${we}`, { headers: hdr() });
          if (!res.ok) return [];
          const data = await res.json();
          return (data?.timesheetsResponseList || []).map(ts => ({
            employee: emp,
            timesheet: {
              id: ts.timesheetId, week_start: ts.weekStart, week_end: ts.weekEnd,
              total_hours: ts.totalHours, status: ts.status,
              comment: ts.clientComment || ts.comment,
              clientComment: ts.clientComment || ts.comment,
              daily_hours: { sun:ts.hoursPerDay.sunday, mon:ts.hoursPerDay.monday, tue:ts.hoursPerDay.tuesday, wed:ts.hoursPerDay.wednesday, thu:ts.hoursPerDay.thursday, fri:ts.hoursPerDay.friday, sat:ts.hoursPerDay.saturday },
              submittedAt: ts.submittedAt,
            },
          }));
        }));
        // Deduplicate by timesheet id — keep only the first occurrence
        const seen = new Set();
        const deduped = results.flat().filter(r => {
          if (seen.has(r.timesheet.id)) return false;
          seen.add(r.timesheet.id);
          return true;
        });
        setAllRows(deduped.sort((a,b) => new Date(b.timesheet.week_start) - new Date(a.timesheet.week_start)));
      } catch(e) { console.error(e); }
      finally { setAllLoading(false); }
    })();
  }, [employees, refreshKey]);

  /* ── fetch this week's timesheets for all employees (dashboard summary) ── */
  useEffect(() => {
    if (!employees.length) return;
    setLoading(true); setError("");
    (async () => {
      try {
        const results = await Promise.all(employees.map(async emp => {
          const res = await fetch(`/timesheets/operations/byDates/${emp.username}?weekStart=${weekStart}&weekEnd=${weekEnd}`, { headers: hdr() });
          if (!res.ok) return { employee: emp, timesheet: null };
          const data = await res.json();
          const ts = (data?.timesheetsResponseList||[])[0];
          if (!ts) return { employee: emp, timesheet: null };
          return {
            employee: emp,
            timesheet: {
              id: ts.timesheetId, week_start: ts.weekStart, week_end: ts.weekEnd,
              total_hours: ts.totalHours, status: ts.status,
              comment: ts.clientComment || ts.comment,
              clientComment: ts.clientComment || ts.comment,
              daily_hours: { sun:ts.hoursPerDay.sunday, mon:ts.hoursPerDay.monday, tue:ts.hoursPerDay.tuesday, wed:ts.hoursPerDay.wednesday, thu:ts.hoursPerDay.thursday, fri:ts.hoursPerDay.friday, sat:ts.hoursPerDay.saturday },
              submittedAt: ts.submittedAt,
            },
          };
        }));
        setRows(results);
        setSelected(new Set());
        setExpandedId(null);
      } catch(e) { setError("Failed to load timesheets."); console.error(e); }
      finally { setLoading(false); }
    })();
  }, [employees, weekStart, weekEnd, refreshKey]);

  /* ── fetch notifications ── */
  const fetchNotifs = useCallback(async () => {
    if (!username) return;
    try {
      const res = await fetch(`/timesheets/notifications/unread/${username}`, { headers: hdr() });
      if (res.ok) setNotifications(await res.json());
    } catch(e) { console.error(e); }
  }, [username]);

  useEffect(() => { fetchNotifs(); const iv=setInterval(fetchNotifs,30000); return ()=>clearInterval(iv); }, [fetchNotifs]);

  const markAllRead = async () => {
    try {
      await fetch(`/timesheets/notifications/read-all/${username}`, { method:"PATCH", headers:hdr() });
      setNotifications([]);
    } catch(e) { console.error(e); }
  };

  /* ── update single timesheet status ── */
  const updateStatus = async (tsId, newStatus) => {
    try {
      const res = await fetch(`/timesheets/operations/updateTimeSheetStatus/${tsId}`, {
        method:"PATCH", headers:{"Content-Type":"application/json",...hdr()},
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error();
      setRows(prev => prev.map(r => r.timesheet?.id===tsId ? {...r, timesheet:{...r.timesheet,status:newStatus}} : r));
      setAllRows(prev => prev.map(r => r.timesheet?.id===tsId ? {...r, timesheet:{...r.timesheet,status:newStatus}} : r));
    } catch(e) { setError("Failed to update status."); }
  };

  /* ── bulk update ── */
  const bulkUpdate = async (newStatus) => {
    const ids = [...selected];
    if (!ids.length) return;
    setUpdateMsg("");
    try {
      const results = await Promise.allSettled(ids.map(id =>
        fetch(`/timesheets/operations/updateTimeSheetStatus/${id}`, {
          method:"PATCH", headers:{"Content-Type":"application/json",...hdr()},
          body: JSON.stringify({ status: newStatus }),
        })
      ));
      const succeeded = ids.filter((_,i)=>results[i].status==="fulfilled"&&results[i].value.ok);
      setAllRows(prev => prev.map(r => r.timesheet&&succeeded.includes(r.timesheet.id) ? {...r,timesheet:{...r.timesheet,status:newStatus}} : r));
      setRows(prev => prev.map(r => r.timesheet&&succeeded.includes(r.timesheet.id) ? {...r,timesheet:{...r.timesheet,status:newStatus}} : r));
      setSelected(new Set());
      const failed = ids.length - succeeded.length;
      if (failed) setUpdateMsg(`${failed} update(s) failed.`);
      setRefreshKey(k=>k+1);
    } catch(e) { setUpdateMsg("Bulk update failed."); }
  };

  /* ── derived stats ── */
  const totalEmp    = employees.length;
  const submitted   = rows.filter(r=>r.timesheet&&r.timesheet.status!=="Not Submitted").length;
  const notSub      = rows.filter(r=>!r.timesheet);
  const subPct      = totalEmp>0 ? Math.round((submitted/totalEmp)*100) : 0;
  const totalHrs    = rows.reduce((s,r)=>s+Number(r.timesheet?.total_hours||0),0);
  const weekPct     = Math.min(Math.round((submitted/Math.max(totalEmp,1))*100),100);

  /* ── filter state ── */

  /* ── filter + paginate — allRows covers full year for approvals/employees views ── */
  const filteredRows = useMemo(() => {
    return allRows.filter(r => {
      const status = r.timesheet?.status || "Not Submitted";
      if (activeTab === "Not Submitted") {
        if (r.timesheet) return false;
      } else {
        if (status !== activeTab) return false;
      }
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!r.employee.fullName?.toLowerCase().includes(q) && !r.employee.username?.toLowerCase().includes(q)) return false;
      }
      // Filter by date range (from/to) only for Approved tab
      if (r.timesheet && activeTab === "Approved" && (approvalFromDate || approvalToDate)) {
        const ws = r.timesheet.week_start;
        const we = r.timesheet.week_end || (ws ? dayjs(ws).add(6, "day").format("YYYY-MM-DD") : null);
        if (approvalFromDate && we && we < approvalFromDate) return false;
        if (approvalToDate && ws && ws > approvalToDate) return false;
      }
      return true;
    });
  }, [allRows, activeTab, searchQuery, approvalFromDate, approvalToDate]);

  // For "Not Submitted" tab, show employees who didn't submit THIS WEEK
  const notSubmittedRows = useMemo(() => {
    return employees
      .filter(emp => !rows.find(r => r.employee?.username === emp.username && r.timesheet))
      .map(emp => ({ employee: emp, timesheet: null }));
  }, [employees, rows]);

  // Use notSubmittedRows for "Not Submitted" tab, filteredRows for others
  const activeFilteredRows = activeTab === "Not Submitted" ? notSubmittedRows : filteredRows;

  const totalPages  = Math.ceil(activeFilteredRows.length / PAGE_SIZE);
  const pagedRows   = activeFilteredRows.slice((currentPage-1)*PAGE_SIZE, currentPage*PAGE_SIZE);
  const allPendingTimesheetIds = allRows.filter(r=>r.timesheet?.status==="Pending").map(r=>r.timesheet.id);
  const allPendingSelected = allPendingTimesheetIds.length>0 && allPendingTimesheetIds.every(id=>selected.has(id));
  const selectedIds = [...selected];

  /* ── recent activity: strictly for employees under this client ── */
  const employeeUsernames = new Set(employees.map(e => (e.username || "").toLowerCase()));
  const employeeNames = new Set(employees.map(e => (e.fullName || "").toLowerCase()));

  const filteredNotifications = notifications.filter(n => {
    const msg = (n.message || "").toLowerCase();
    return Array.from(employeeUsernames).some(u => u && msg.includes(u)) ||
           Array.from(employeeNames).some(name => name && msg.includes(name));
  });

  const recentActivity = [
    ...filteredNotifications.map(n => ({
      icon: n.type?.includes("APPROVED") ? "green" : n.type?.includes("REJECTED") ? "red" : "blue",
      text: n.message || "Notification",
      time: n.createdAt ? dayjs(n.createdAt).format("MMM D, YYYY · h:mm A") : "",
      rawTime: n.createdAt ? new Date(n.createdAt).getTime() : 0,
    })),
    ...allRows
      .filter(r => r.timesheet?.submittedAt)
      .map(r => ({
        icon: r.timesheet.status === "Approved" ? "green" : r.timesheet.status === "Rejected" ? "red" : "amber",
        text: `${r.employee.fullName} submitted timesheet`,
        time: dayjs(r.timesheet.submittedAt).format("MMM D, YYYY · h:mm A"),
        rawTime: new Date(r.timesheet.submittedAt).getTime(),
      })),
  ]
  .sort((a, b) => b.rawTime - a.rawTime)
  .filter((item, idx, self) => idx === self.findIndex(t => t.text === item.text && t.time === item.time))
  .slice(0, 4);

  // Date-range-filtered allRows for Approved tab count only
  const approvedFilteredRows = useMemo(() => {
    if (!approvalFromDate && !approvalToDate) return allRows;
    return allRows.filter(r => {
      if (!r.timesheet) return false;
      const ws = r.timesheet.week_start;
      const we = r.timesheet.week_end || (ws ? dayjs(ws).add(6, "day").format("YYYY-MM-DD") : null);
      if (approvalFromDate && we && we < approvalFromDate) return false;
      if (approvalToDate && ws && ws > approvalToDate) return false;
      return true;
    });
  }, [allRows, approvalFromDate, approvalToDate]);

  const tabCounts = {
    Pending:        allRows.filter(r=>r.timesheet?.status==="Pending").length,
    Approved:       approvedFilteredRows.filter(r=>r.timesheet?.status==="Approved").length,
    Rejected:       allRows.filter(r=>r.timesheet?.status==="Rejected").length,
  };

  const COLORS = ["#2563EB","#10B981","#F59E0B","#8B5CF6"];

  const handleNavClick = (key) => { setActiveNav(key); setExpandedId(null); };

  /* ── Shared approvals table ── */
  const renderApprovalsTable = (tableRows, showBulkActions=true) => (
    <div className="cm-approvals-card">
      <div className="cm-approvals-header">
        <h3 className="cm-approvals-title">Timesheet Management</h3>
        <div className="cm-tabs" role="tablist">
          {STATUS_TABS.map(tab => (
            <button key={tab} role="tab" aria-selected={activeTab===tab}
              className={`cm-tab ${activeTab===tab?"active":""}`}
              onClick={()=>{setActiveTab(tab);setExpandedId(null);}}>
              {tab}{tabCounts[tab]>0?` (${tabCounts[tab]})`:``}
            </button>
          ))}
        </div>
      </div>

      <div className="cm-toolbar">
        <div className="cm-search-box">
          <Icon.Search size={13} color="#94A3B8"/>
          <input placeholder="Search employees..." value={searchQuery}
            onChange={e=>setSearchQuery(e.target.value)}
            aria-label="Search employees"/>
        </div>
        {showBulkActions && activeTab==="Pending" && selectedIds.length>0 && (
          <>
            <button className="cm-toolbar-btn" style={{background:"#ECFDF5",borderColor:"#A7F3D0",color:"#059669"}}
              onClick={()=>bulkUpdate("Approved")}>
              <Icon.Check size={12} color="currentColor"/> Approve ({selectedIds.length})
            </button>
            <button className="cm-toolbar-btn" style={{background:"#FEF2F2",borderColor:"#FECACA",color:"#DC2626"}}
              onClick={()=>bulkUpdate("Rejected")}>
              <Icon.X size={12} color="currentColor"/> Reject ({selectedIds.length})
            </button>
          </>
        )}
        {showBulkActions && activeTab==="Pending" && allPendingTimesheetIds.length>0 && (
          <button className="cm-toolbar-btn" onClick={()=>{
            setSelected(prev=>allPendingSelected?new Set():new Set(allPendingTimesheetIds));
          }}>
            {allPendingSelected?"Deselect All":"Select All Pending"}
          </button>
        )}

      </div>

      {(loading || allLoading) ? (
        <div className="cm-empty">
          <span style={{display:"inline-block",width:20,height:20,border:"2px solid #E2EEFF",borderTopColor:"#2563EB",borderRadius:"50%",animation:"spin 0.7s linear infinite"}}/>
        </div>
      ) : tableRows.length===0 ? (
        <div className="cm-empty">
          <Icon.CheckCircle size={32} color="#A7F3D0" strokeWidth={1.5} style={{marginBottom:8}}/>
          <p>No timesheets found for this filter.</p>
        </div>
      ) : (
        <table className="cm-table">
          <thead>
            <tr>
              {showBulkActions && activeTab==="Pending" && <th><input type="checkbox" className="cm-check"
                checked={allPendingSelected&&allPendingTimesheetIds.length>0}
                onChange={()=>setSelected(allPendingSelected?new Set():new Set(allPendingTimesheetIds))} aria-label="Select all"/></th>}
              <th>Employee</th>
              <th>Week Range</th>
              <th>Total Hours</th>
              <th>Submitted On</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {tableRows.map(({employee,timesheet}) => {
              const status = timesheet?.status||"Not Submitted";
              const statusKey = status.toLowerCase().replace(/\s+/g,"-");
              const isOpen = expandedId===timesheet?.id;
              const isPending = status==="Pending";
              return (
                <React.Fragment key={timesheet?.id||employee.username}>
                  <tr style={{background:isOpen?"#FAFBFF":undefined}}>
                    {showBulkActions && activeTab==="Pending" && (
                      <td>
                        <input type="checkbox" className="cm-check"
                          checked={timesheet?selected.has(timesheet.id):false}
                          disabled={!isPending}
                          onChange={()=>{if(!timesheet)return;setSelected(prev=>{const n=new Set(prev);n.has(timesheet.id)?n.delete(timesheet.id):n.add(timesheet.id);return n;});}}
                          aria-label={`Select ${employee.fullName}`}/>
                      </td>
                    )}
                    <td>
                      <div className="cm-emp-cell">
                        <div className="cm-emp-avatar">{(employee.fullName||"?")[0].toUpperCase()}</div>
                        <div>
                          <div className="cm-emp-name">{employee.fullName}</div>
                          <div className="cm-emp-role">{employee.username}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{fontSize:"0.8rem",color:"#475569"}}>
                      {timesheet ? `${dayjs(timesheet.week_start).format("MMM D")} – ${dayjs(timesheet.week_end).format("MMM D, YYYY")}` : "—"}
                    </td>
                    <td><span className="cm-hours-val">{timesheet ? `${timesheet.total_hours}h 00m` : "—"}</span></td>
                    <td style={{fontSize:"0.75rem",color:"#94A3B8"}}>
                      {timesheet?.submittedAt ? dayjs(timesheet.submittedAt).format("MMM D, YYYY") : "—"}
                    </td>
                    <td>
                      <div className="cm-action-cell">
                        <span className={`cm-status-pill ${statusKey}`}>{status}</span>
                        {isPending && (
                          <>
                            <button className="cm-action-btn approve" title="Approve"
                              onClick={()=>updateStatus(timesheet.id,"Approved")}>✓</button>
                            <button className="cm-action-btn reject" title="Reject"
                              onClick={()=>updateStatus(timesheet.id,"Rejected")}>✕</button>
                          </>
                        )}
                        {timesheet && (
                          <button className="cm-action-btn view" title="View details"
                            onClick={()=>setExpandedId(isOpen?null:timesheet.id)}>
                            <Icon.Eye size={12} color="currentColor"/>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {isOpen && timesheet && (
                    <tr className="cm-expanded-row">
                      <td colSpan={showBulkActions&&activeTab==="Pending"?6:5}>
                        <ClientManagerTimesheet
                          timesheets={[timesheet]}
                          showActionButtons={isPending}
                          onStatusChange={(id,s)=>{
                            setRows(prev=>prev.map(r=>r.timesheet?.id===id?{...r,timesheet:{...r.timesheet,status:s}}:r));
                            setAllRows(prev=>prev.map(r=>r.timesheet?.id===id?{...r,timesheet:{...r.timesheet,status:s}}:r));
                          }}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      <div className="cm-table-footer">
        <span>Showing {activeFilteredRows.length===0?0:(currentPage-1)*PAGE_SIZE+1} to {Math.min(currentPage*PAGE_SIZE,activeFilteredRows.length)} of {activeFilteredRows.length} entries</span>
        <div className="cm-table-footer-right">
          <button className="cm-page-btn" disabled={currentPage===1} onClick={()=>setCurrentPage(p=>p-1)}>‹</button>
          {Array.from({length:Math.min(totalPages,5)},(_,i)=>i+1).map(p=>(
            <button key={p} className={`cm-page-btn ${p===currentPage?"active":""}`} onClick={()=>setCurrentPage(p)}>{p}</button>
          ))}
          <button className="cm-page-btn" disabled={currentPage>=totalPages} onClick={()=>setCurrentPage(p=>p+1)}>›</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="cm-shell">

      {/* ══ SIDEBAR ══ */}
      <nav className="cm-sidebar" aria-label="Client manager navigation">
        <p className="cm-nav-section-label">Navigation</p>
        {NAV.map(item => (
          <button key={item.key} className={`cm-nav-item ${activeNav===item.key?"active":""}`}
            onClick={()=>handleNavClick(item.key)}
            aria-current={activeNav===item.key?"page":undefined}>
            {item.icon==="Home"        && <Icon.Home        size={15} color={activeNav===item.key?"#2563EB":"#94A3B8"}/>}
            {item.icon==="CheckCircle" && <Icon.CheckCircle size={15} color={activeNav===item.key?"#2563EB":"#94A3B8"}/>}
            {item.icon==="Users"       && <Icon.Users       size={15} color={activeNav===item.key?"#2563EB":"#94A3B8"}/>}
            {item.icon==="BarChart"    && <Icon.BarChart    size={15} color={activeNav===item.key?"#2563EB":"#94A3B8"}/>}
            {item.icon==="Bell"        && <Icon.Bell        size={15} color={activeNav===item.key?"#2563EB":"#94A3B8"}/>}
            {item.icon==="Badge"       && <Icon.Badge       size={15} color={activeNav===item.key?"#2563EB":"#94A3B8"}/>}
            {item.icon==="MessageSquare" && <Icon.MessageSquare size={15} color={activeNav===item.key?"#2563EB":"#94A3B8"}/>}
            {item.label}
            {item.key==="notifications"&&notifications.length>0&&(
              <span style={{marginLeft:"auto",background:"#EF4444",color:"#fff",fontSize:"0.6rem",fontWeight:700,padding:"1px 5px",borderRadius:999}}>{notifications.length}</span>
            )}
          </button>
        ))}

        {/* <div className="cm-sidebar-footer">
          <div className="cm-help-card">
            <div style={{width:28,height:28,borderRadius:50,background:"#EFF6FF",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 6px"}}>
              <Icon.AlertCircle size={14} color="#2563EB"/>
            </div>
            <h4>Need Help?</h4>
            <p>We're here to assist you</p>
            <button className="cm-help-btn" onClick={()=>setShowSupport(true)}>Contact Support</button>
          </div>
        </div> */}
      </nav>

      {/* ══ MAIN ══ */}
      <div className="cm-main">

        {/* Bell icon for notifications */}
        <div style={{position:"fixed",top:13,right:130,zIndex:1400}}>
          <button onClick={()=>setShowNotifications(!showNotifications)}
            style={{position:"relative",width:34,height:34,borderRadius:8,background:"transparent",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}
            aria-label="Notifications">
            <Icon.Bell size={18} color="#64748B"/>
            {notifications.length>0&&(
              <span style={{position:"absolute",top:2,right:2,width:16,height:16,borderRadius:999,background:"#EF4444",color:"#fff",fontSize:"0.6rem",fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center"}}>{notifications.length}</span>
            )}
          </button>
        </div>

        {/* Notifications dropdown */}
        {showNotifications&&(
          <>
            <div style={{position:"fixed",inset:0,zIndex:1500,background:"transparent"}} onClick={()=>setShowNotifications(false)}/>
            <div style={{position:"fixed",top:52,right:16,width:340,background:"#fff",border:"1.5px solid #E2EEFF",borderRadius:14,boxShadow:"0 8px 32px rgba(37,99,235,0.14)",zIndex:1600,overflow:"hidden"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 16px",borderBottom:"1px solid #F1F5F9",fontSize:"0.875rem",fontWeight:700,color:"#1E293B"}}>
                <span>Notifications</span>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  {notifications.length>0&&<button style={{fontSize:"0.75rem",color:"#2563EB",background:"none",border:"none",cursor:"pointer",fontWeight:600}} onClick={()=>{markAllRead();setShowNotifications(false);}}>Mark all read</button>}
                  <button style={{width:24,height:24,borderRadius:6,background:"transparent",border:"1px solid #E2EEFF",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}
                    onClick={()=>setShowNotifications(false)} aria-label="Close"><Icon.X size={11} color="#64748B"/></button>
                </div>
              </div>
              {notifications.length===0
                ? <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:10,padding:"36px 20px",color:"#64748B",fontSize:"0.84rem"}}><Icon.CheckCircle size={24} color="#86efac"/> You're all caught up!</div>
                : <ul style={{listStyle:"none",maxHeight:320,overflowY:"auto",margin:0,padding:0}}>
                    {notifications.map(n=>(
                      <li key={n.id} style={{display:"flex",flexDirection:"column",gap:4,padding:"12px 16px",borderBottom:"1px solid #F8FBFF",borderLeft:`3px solid ${n.type?.includes("APPROVED")?"#10B981":"#2563EB"}`}}>
                        <span style={{fontSize:"0.8125rem",fontWeight:500,color:"#1E293B"}}>{n.message}</span>
                        <span style={{fontSize:"0.72rem",color:"#94A3B8"}}>{n.createdAt?new Date(n.createdAt).toLocaleDateString():""}</span>
                      </li>
                    ))}
                  </ul>}
            </div>
          </>
        )}
        <div className="cm-center">

          {/* ── NON-DASHBOARD VIEWS ── */}

          {activeNav==="approvals" && (
            <>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:12,marginBottom:12,paddingTop:4}}>
                <div style={{fontWeight:700,fontSize:"0.75rem",textTransform:"uppercase",letterSpacing:"0.08em",color:"#2563EB"}}>Timesheet Management</div>
                {activeTab==="Approved" && (
                <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 14px",background:"#fff",border:"1px solid #E2EEFF",borderRadius:10,boxShadow:"0 1px 4px rgba(37,99,235,0.05)"}}>
                    <Icon.Calendar size={14} color="#64748B"/>
                    <label htmlFor="cm-approvals-from" style={{fontSize:"0.78rem",fontWeight:600,color:"#64748B",whiteSpace:"nowrap"}}>From:</label>
                    <input id="cm-approvals-from" type="date" value={approvalFromDate}
                      onChange={e=>setApprovalFromDate(e.target.value)}
                      style={{border:"none",background:"transparent",fontSize:"0.84rem",color:"#1E293B",outline:"none",fontFamily:"var(--font-sans)"}}/>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 14px",background:"#fff",border:"1px solid #E2EEFF",borderRadius:10,boxShadow:"0 1px 4px rgba(37,99,235,0.05)"}}>
                    <Icon.Calendar size={14} color="#64748B"/>
                    <label htmlFor="cm-approvals-to" style={{fontSize:"0.78rem",fontWeight:600,color:"#64748B",whiteSpace:"nowrap"}}>To:</label>
                    <input id="cm-approvals-to" type="date" value={approvalToDate}
                      onChange={e=>setApprovalToDate(e.target.value)}
                      style={{border:"none",background:"transparent",fontSize:"0.84rem",color:"#1E293B",outline:"none",fontFamily:"var(--font-sans)"}}/>
                  </div>
                  {(approvalFromDate || approvalToDate) && (
                    <button onClick={()=>{setApprovalFromDate("");setApprovalToDate("");}}
                      style={{padding:"7px 14px",borderRadius:8,border:"1.5px solid #FECACA",background:"#FEF2F2",fontSize:"0.8rem",fontWeight:600,color:"#DC2626",cursor:"pointer"}}>
                      Clear
                    </button>
                  )}
                </div>
                )}
              </div>
              {renderApprovalsTable(pagedRows, true)}
            </>
          )}

          {activeNav==="employees" && (
            <div className="cm-approvals-card">
              <div className="cm-approvals-header">
                <h3 className="cm-approvals-title">All Employees</h3>
              </div>
              <table className="cm-table">
                <thead><tr><th>Employee</th><th>Username</th><th>Start Date</th><th>Status</th><th>Latest Timesheet</th><th>Action</th></tr></thead>
                <tbody>
                  {employees.map(emp=>{
                    const empRows = allRows.filter(r=>r.employee.username===emp.username&&r.timesheet);
                    const latestTs = empRows[0]?.timesheet;
                    const status = latestTs?.status||"Not Submitted";
                    const sk = status.toLowerCase().replace(/\s+/g,"-");
                    return (
                      <React.Fragment key={emp.username}>
                      <tr>
                        <td><div className="cm-emp-cell">
                          <div className="cm-emp-avatar">{(emp.fullName||"?")[0].toUpperCase()}</div>
                          <div><div className="cm-emp-name">{emp.fullName}</div></div>
                        </div></td>
                        <td style={{fontSize:"0.8rem",color:"#475569"}}>{emp.username}</td>
                        <td style={{fontSize:"0.8rem",color:"#475569"}}>{emp.startDate ? dayjs(emp.startDate).format("MMM D, YYYY") : "—"}</td>
                        <td><span className={`cm-status-pill ${sk}`}>{status}</span></td>
                        <td style={{fontSize:"0.8rem",color:"#475569"}}>{latestTs ? `${dayjs(latestTs.week_start).format("MMM D")} – ${dayjs(latestTs.week_end).format("MMM D")}` : "—"}</td>
                        <td>
                          <button className="cm-action-btn view" title="View timesheets" onClick={()=>setExpandedId(expandedId===emp.username?null:emp.username)}><Icon.Eye size={12} color="currentColor"/></button>
                        </td>
                      </tr>
                      {expandedId===emp.username && (
                        <tr><td colSpan={6} style={{padding:"16px 20px",background:"#F8FBFF",borderTop:"1px solid #E2EEFF"}}>
                          <h4 style={{fontSize:"0.84rem",fontWeight:700,color:"#0F172A",marginBottom:10}}>All Timesheets for {emp.fullName} ({empRows.length})</h4>
                          {empRows.length===0 ? (
                            <p style={{fontSize:"0.8rem",color:"#94A3B8"}}>No timesheets found.</p>
                          ) : (
                            <table style={{width:"100%",fontSize:"0.78rem",borderCollapse:"collapse"}}>
                              <thead><tr style={{color:"#64748B",fontWeight:700}}><th style={{padding:"6px 8px",textAlign:"left"}}>Week Range</th><th style={{padding:"6px 8px",textAlign:"left"}}>Hours</th><th style={{padding:"6px 8px",textAlign:"left"}}>Status</th><th style={{padding:"6px 8px",textAlign:"left"}}>Daily Breakdown</th></tr></thead>
                              <tbody>
                                {empRows.sort((a,b)=>new Date(a.timesheet.week_start)-new Date(b.timesheet.week_start)).map(({timesheet:ts})=>(
                                  <tr key={ts.id} style={{borderTop:"1px solid #E2EEFF"}}>
                                    <td style={{padding:"6px 8px"}}>{dayjs(ts.week_start).format("MMM D")} – {dayjs(ts.week_end).format("MMM D")}</td>
                                    <td style={{padding:"6px 8px",fontWeight:600}}>{ts.total_hours}h</td>
                                    <td style={{padding:"6px 8px"}}><span className={`cm-status-pill ${ts.status?.toLowerCase().replace(/\s+/g,"-")}`}>{ts.status}</span></td>
                                    <td style={{padding:"6px 8px"}}>
                                      <div style={{display:"flex",gap:6}}>
                                        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d,i)=>(
                                          <div key={d} style={{textAlign:"center",minWidth:32}}>
                                            <div style={{fontWeight:600,color:"#64748B",fontSize:"0.7rem"}}>{d}</div>
                                            <div style={{fontWeight:500,color:"#94A3B8",fontSize:"0.68rem"}}>{dayjs(ts.week_start).day(0).add(i,"day").format("M/D")}</div>
                                            <div style={{fontWeight:600,color:"#0F172A"}}>{ts.daily_hours?.[d.toLowerCase().slice(0,3)]??0}h</div>
                                          </div>
                                        ))}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td></tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              <div className="cm-table-footer"><span>{employees.length} employees</span></div>
            </div>
          )}

          {activeNav==="reports" && (
            <div style={{display:"flex",flexDirection:"column",gap:16}}>
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
                {[
                  {label:"Total Employees",value:totalEmp,   color:"#2563EB",bg:"#EFF6FF"},
                  {label:"Total Submitted", value:allRows.filter(r=>r.timesheet).length, color:"#10B981",bg:"#ECFDF5"},
                  {label:"Pending",         value:allRows.filter(r=>r.timesheet?.status==="Pending").length,color:"#F59E0B",bg:"#FFFBEB"},
                  {label:"Total Hours",     value:`${allRows.reduce((s,r)=>s+Number(r.timesheet?.total_hours||0),0)}h`,color:"#8B5CF6",bg:"#F5F3FF"},
                ].map(c=>(
                  <div key={c.label} style={{background:"#fff",border:"1px solid #E2EEFF",borderRadius:14,padding:"16px",boxShadow:"0 1px 4px rgba(37,99,235,0.05)"}}>
                    <div style={{width:36,height:36,borderRadius:10,background:c.bg,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:10}}>
                      <Icon.BarChart size={17} color={c.color}/>
                    </div>
                    <div style={{fontSize:"1.5rem",fontWeight:800,color:"#0F172A",letterSpacing:"-0.03em"}}>{c.value}</div>
                    <div style={{fontSize:"0.72rem",color:"#64748B",fontWeight:500,marginTop:2}}>{c.label}</div>
                  </div>
                ))}
              </div>
              <div className="cm-summary-card">
                <div className="cm-summary-header"><h4>Team Summary — All Time</h4></div>
                <div className="cm-summary-list">
                  {employees.slice(0,8).map((emp,i)=>{
                    const empRows = allRows.filter(r=>r.employee.username===emp.username&&r.timesheet);
                    const empHrs  = empRows.reduce((s,r)=>s+Number(r.timesheet.total_hours||0),0);
                    const empPending = empRows.filter(r=>r.timesheet.status==="Pending").length;
                    const maxH = Math.max(...employees.map(e=>allRows.filter(r=>r.employee.username===e.username&&r.timesheet).reduce((s,r)=>s+Number(r.timesheet.total_hours||0),0)),1);
                    const pct = Math.min(Math.round((empHrs/maxH)*100),100);
                    return (
                      <div key={emp.username} className="cm-summary-item">
                        <h5>{emp.fullName}</h5>
                        <div className="cm-summary-sub">{empHrs}h total · {empRows.length} weeks · {empPending} pending</div>
                        <div className="cm-summary-bar"><div className="cm-summary-fill" style={{width:`${pct}%`,background:COLORS[i%COLORS.length]}}/></div>
                        <div className="cm-summary-pct" style={{color:COLORS[i%COLORS.length]}}>{empHrs}h</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {activeNav==="feedback" && (
            <ClientFeedbackView username={username} name={name}/>
          )}

          {activeNav==="notifications" && (
            <div className="cm-approvals-card">
              <div className="cm-approvals-header" style={{paddingBottom:14}}>
                <h3 className="cm-approvals-title">Notifications</h3>
                {notifications.length>0&&<button className="cm-view-all" onClick={markAllRead}>Mark all read</button>}
              </div>
              {notifications.length===0 ? (
                <div className="cm-empty">
                  <Icon.CheckCircle size={28} color="#A7F3D0"/>
                  <p style={{marginTop:8}}>You're all caught up!</p>
                </div>
              ) : (
                <div style={{padding:"0 20px 16px",display:"flex",flexDirection:"column",gap:10}}>
                  {notifications.map(n=>(
                    <div key={n.id} style={{display:"flex",gap:10,padding:"10px 14px",background:"#F8FBFF",borderRadius:10,borderLeft:`3px solid ${n.type?.includes("APPROVED")?"#10B981":"#2563EB"}`}}>
                      <div style={{flex:1,fontSize:"0.8125rem",color:"#1E293B",fontWeight:500}}>{n.message}</div>
                      <div style={{fontSize:"0.72rem",color:"#94A3B8",whiteSpace:"nowrap"}}>{n.createdAt?dayjs(n.createdAt).format("MMM D"):""}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeNav==="settings" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ background: "#fff", border: "1px solid #E2EEFF", borderRadius: 14, padding: "24px", boxShadow: "0 1px 4px rgba(37,99,235,0.06)" }}>
                <h4 style={{ fontSize: "0.9375rem", fontWeight: 700, color: "#0F172A", marginBottom: 20 }}>Profile Information</h4>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
                  {[
                    { label: "Full Name", value: profileData?.name || name || "—" },
                    { label: "Username", value: username || "—" },
                    { label: "Email", value: profileData?.email || settingsEmail || "—" },
                    { label: "Role", value: "Client Manager" },
                    { label: "Mobile Number", value: profileData?.mobileNumber || "—" },
                    { label: "Date of Birth", value: (profileData?.dateOfBirth || dateOfBirth) ? dayjs(profileData?.dateOfBirth || dateOfBirth).format("MMM D, YYYY") : "Not set" },
                    { label: "Timezone", value: profileData?.timezone || "—" },
                    { label: "Team Size", value: `${totalEmp} employees` },
                  ].map(f => (
                    <div key={f.label} style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                      <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em" }}>{f.label}</label>
                      <div style={{ padding: "10px 14px", background: "#F8FAFC", border: "1.5px solid #E2E8F0", borderRadius: 10, fontSize: "0.875rem", fontWeight: 500, color: "#0F172A" }}>{f.value}</div>
                    </div>
                  ))}
                </div>

                {!hasExistingDob && (
                  <div style={{ borderTop: "1px solid #EFF6FF", paddingTop: 20, marginTop: 10, display: "flex", flexDirection: "column", gap: 12 }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <h5 style={{ fontSize: "0.88rem", fontWeight: 700, color: "#0F172A", margin: 0 }}>Set Your Date of Birth</h5>
                      <p style={{ fontSize: "0.78rem", color: "#64748B", margin: 0 }}>You can set your Date of Birth once. Once saved, it will be locked to your account.</p>
                    </div>

                    {dobMsg && (
                      <div style={{
                        padding: "8px 14px",
                        borderRadius: 8,
                        fontSize: "0.82rem",
                        fontWeight: 600,
                        background: dobMsg.type === "success" ? "#ECFDF5" : "#FEF2F2",
                        color: dobMsg.type === "success" ? "#059669" : "#DC2626",
                        border: `1px solid ${dobMsg.type === "success" ? "#A7F3D0" : "#FECACA"}`
                      }}>
                        {dobMsg.text}
                      </div>
                    )}

                    <div style={{ display: "flex", alignItems: "flex-end", gap: 12, maxWidth: 380 }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 5, flex: 1 }}>
                        <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "#475569" }}>
                          Date of Birth <span style={{ color: "#EF4444" }}>*</span>
                        </label>
                        <input
                          type="date"
                          value={dateOfBirth}
                          onChange={(e) => setDateOfBirth(e.target.value)}
                          style={{
                            padding: "10px 14px",
                            background: "#fff",
                            border: "1.5px solid #CBD5E1",
                            borderRadius: 10,
                            fontSize: "0.875rem",
                            fontWeight: 500,
                            color: "#0F172A",
                            outline: "none",
                            fontFamily: "inherit"
                          }}
                        />
                      </div>
                      <button
                        onClick={handleSaveDob}
                        disabled={savingDob || !dateOfBirth}
                        style={{
                          padding: "11px 22px",
                          background: "#2563EB",
                          color: "#fff",
                          border: "none",
                          borderRadius: 10,
                          fontSize: "0.85rem",
                          fontWeight: 700,
                          cursor: (savingDob || !dateOfBirth) ? "not-allowed" : "pointer",
                          opacity: (savingDob || !dateOfBirth) ? 0.6 : 1,
                          height: 42,
                          whiteSpace: "nowrap"
                        }}
                      >
                        {savingDob ? "Saving..." : "Save DOB"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div style={{ background: "#F8FBFF", border: "1px solid #E2EEFF", borderRadius: 10, padding: "14px 18px" }}>
                <p style={{ fontSize: "0.8rem", color: "#64748B", margin: 0 }}>ℹ️ To update other profile details, please contact your employer.</p>
              </div>
            </div>
          )}

          {/* ── DASHBOARD ── */}
          {activeNav==="dashboard" && (<>

            {/* Hero */}
            <motion.div className="cm-hero" initial={{opacity:0,y:14}} animate={{opacity:1,y:0}} transition={{duration:0.35}}>
              <div className="cm-hero-text">
                <h1 className="cm-hero-title">{getGreeting()}, {name?.split(" ")[0]||"Client Manager"}! 👋</h1>
                <p className="cm-hero-sub">Here's what's happening with your team this week.</p>
              </div>
              <div className="cm-hero-illo" aria-hidden="true">
                <div className="cm-hero-illo-inner"><Icon.Users size={44} color="#2563EB" strokeWidth={1.2}/></div>
                <div className="cm-hero-illo-float"><Icon.CheckCircle size={20} color="#10B981" strokeWidth={1.5}/></div>
              </div>
            </motion.div>

            {/* Stats */}
            <div className="cm-stats-row">
              {[
                {label:"Total Employees",sub:`${totalEmp} in your team`,subCls:"",val:totalEmp,cls:"blue",icon:"Users"},
                {label:"Timesheets Submitted",sub:`This Week · ${subPct}%`,subCls:subPct>=70?"green":"amber",val:submitted,cls:"green", icon:"CheckCircle"},
                {label:"Pending Approvals",sub:`Need your review`,subCls:"amber",val:tabCounts.Pending,  cls:"amber", icon:"Clock"},
              ].map((s,i)=>(
                <motion.div key={s.label} className="cm-stat-card"
                  initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} transition={{delay:0.1+i*0.06,duration:0.3}}
                  onClick={()=>{ if(s.label.includes("Pending")) { setActiveNav("approvals"); setActiveTab("Pending"); } }}>
                  <div className={`cm-stat-icon ${s.cls}`}>
                    {s.icon==="Users"        && <Icon.Users        size={18} color="#2563EB"/>}
                    {s.icon==="CheckCircle"  && <Icon.CheckCircle  size={18} color="#10B981"/>}
                    {s.icon==="Clock"        && <Icon.Clock        size={18} color="#F59E0B"/>}
                  </div>
                  <div>
                    <div className="cm-stat-val">{s.val}</div>
                    <div className="cm-stat-label">{s.label}</div>
                    <div className={`cm-stat-sub ${s.subCls}`}>{s.sub}</div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Error/Update messages */}
            <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
              {error&&<span style={{fontSize:"0.8rem",color:"#EF4444",fontWeight:500}}>{error}</span>}
              {updateMsg&&<span style={{fontSize:"0.8rem",color:"#F59E0B",fontWeight:500}}>{updateMsg}</span>}
            </div>

            {/* Date picker for Approved tab */}
            {activeTab==="Approved" && (
              <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 14px",background:"#fff",border:"1px solid #E2EEFF",borderRadius:10,boxShadow:"0 1px 4px rgba(37,99,235,0.05)"}}>
                  <Icon.Calendar size={14} color="#64748B"/>
                  <label htmlFor="cm-dash-from" style={{fontSize:"0.78rem",fontWeight:600,color:"#64748B",whiteSpace:"nowrap"}}>From:</label>
                  <input id="cm-dash-from" type="date" value={approvalFromDate}
                    onChange={e=>setApprovalFromDate(e.target.value)}
                    style={{border:"none",background:"transparent",fontSize:"0.84rem",color:"#1E293B",outline:"none",fontFamily:"var(--font-sans)"}}/>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 14px",background:"#fff",border:"1px solid #E2EEFF",borderRadius:10,boxShadow:"0 1px 4px rgba(37,99,235,0.05)"}}>
                  <Icon.Calendar size={14} color="#64748B"/>
                  <label htmlFor="cm-dash-to" style={{fontSize:"0.78rem",fontWeight:600,color:"#64748B",whiteSpace:"nowrap"}}>To:</label>
                  <input id="cm-dash-to" type="date" value={approvalToDate}
                    onChange={e=>setApprovalToDate(e.target.value)}
                    style={{border:"none",background:"transparent",fontSize:"0.84rem",color:"#1E293B",outline:"none",fontFamily:"var(--font-sans)"}}/>
                </div>
                {(approvalFromDate || approvalToDate) && (
                  <button onClick={()=>{setApprovalFromDate("");setApprovalToDate("");}}
                    style={{padding:"7px 14px",borderRadius:8,border:"1.5px solid #FECACA",background:"#FEF2F2",fontSize:"0.8rem",fontWeight:600,color:"#DC2626",cursor:"pointer"}}>
                    Clear
                  </button>
                )}
              </div>
            )}

            {/* Approvals table */}
            {renderApprovalsTable(pagedRows, true)}
          </>)}
        </div>

        {/* ── RIGHT PANEL (dashboard only) ── */}
        {activeNav==="dashboard" && (
        <div className="cm-right">

          {/* Progress */}
          <motion.div className="cm-progress-card" initial={{opacity:0,x:14}} animate={{opacity:1,x:0}} transition={{delay:0.2,duration:0.3}}>
            <div className="cm-panel-title">This Week Progress</div>
            <div className="cm-progress-circle-wrap">
              <CircProg pct={weekPct}/>
              <div className="cm-progress-info">
                <h4>{totalHrs}h</h4>
                <p>Total Hours Logged</p>
                <div className="cm-progress-sub">{submitted}<span>/{totalEmp}</span></div>
                <p style={{fontSize:"0.68rem",color:"#64748B"}}>Timesheets Submitted</p>
              </div>
            </div>
            <button className="cm-view-analytics" onClick={()=>handleNavClick("reports")}>View Full Analytics →</button>
          </motion.div>

          {/* Not submitted */}
          <motion.div className="cm-not-submitted-card" initial={{opacity:0,x:14}} animate={{opacity:1,x:0}} transition={{delay:0.25,duration:0.3}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
              <div className="cm-panel-title" style={{margin:0}}>Employees Not Submitted</div>
              {notSub.length>0&&<button className="cm-view-all" onClick={()=>{setActiveNav("approvals");setActiveTab("Pending");}}>View All</button>}
            </div>
            {notSub.length===0 ? (
              <div className="cm-not-submitted-empty">
                <Icon.CheckCircle size={24} color="#A7F3D0"/>
                <p>Great! All employees have submitted their timesheets.</p>
              </div>
            ) : (
              <div className="cm-ns-list">
                {notSub.slice(0,4).map(r=>(
                  <div key={r.employee.username} className="cm-ns-item">
                    <div className="cm-ns-avatar">{(r.employee.fullName||"?")[0].toUpperCase()}</div>
                    <span className="cm-ns-name">{r.employee.fullName}</span>
                  </div>
                ))}
                {notSub.length>4&&<p style={{fontSize:"0.72rem",color:"#94A3B8",marginTop:4}}>+{notSub.length-4} more</p>}
              </div>
            )}
          </motion.div>

          {/* Recent activity */}
          <motion.div className="cm-activity-card" initial={{opacity:0,x:14}} animate={{opacity:1,x:0}} transition={{delay:0.3,duration:0.3}}>
            <div className="cm-activity-header">
              <div className="cm-panel-title" style={{margin:0}}>Recent Activity</div>
              <button className="cm-view-all" onClick={()=>setShowNotifications(true)}>View All</button>
            </div>
            <div className="cm-activity-list">
              {recentActivity.length===0 ? (
                <p style={{fontSize:"0.78rem",color:"#94A3B8"}}>No recent activity.</p>
              ) : recentActivity.map((a,i)=>(
                <div key={i} className="cm-activity-item">
                  <div className={`cm-activity-icon ${a.icon}`}>
                    {a.icon==="green"&&<Icon.CheckCircle size={13} color="#10B981"/>}
                    {a.icon==="blue" &&<Icon.FileText    size={13} color="#2563EB"/>}
                    {a.icon==="amber"&&<Icon.Clock       size={13} color="#F59E0B"/>}
                  </div>
                  <div className="cm-activity-text"><p>{a.text}</p><span>{a.time}</span></div>
                </div>
              ))}
            </div>
          </motion.div>

        </div>
        )}
      </div>

      {/* ══ SUPPORT MODAL ══ */}
      <AnimatePresence>
        {showSupport&&(
          <motion.div style={{position:"fixed",inset:0,background:"rgba(15,23,42,0.5)",backdropFilter:"blur(4px)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:2000,padding:20}}
            initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={()=>setShowSupport(false)}>
            <motion.div style={{background:"#fff",borderRadius:18,boxShadow:"0 16px 48px rgba(37,99,235,0.15)",width:"100%",maxWidth:420,overflow:"hidden"}}
              initial={{opacity:0,scale:0.96,y:20}} animate={{opacity:1,scale:1,y:0}} exit={{opacity:0,scale:0.96,y:20}} transition={{duration:0.25}}
              onClick={e=>e.stopPropagation()}>
              <div style={{background:"linear-gradient(135deg,#1e40af,#2563eb)",padding:"22px 24px",position:"relative"}}>
                <h2 style={{fontSize:"1.1rem",fontWeight:700,color:"#fff",marginBottom:4}}>Contact Support</h2>
                <p style={{fontSize:"0.8rem",color:"rgba(255,255,255,0.72)"}}>We're here to help</p>
                <button style={{position:"absolute",top:14,right:14,width:26,height:26,borderRadius:6,background:"rgba(255,255,255,0.12)",border:"1px solid rgba(255,255,255,0.18)",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}
                  onClick={()=>setShowSupport(false)}><Icon.X size={11} color="#fff"/></button>
              </div>
              <div style={{padding:"20px 24px",display:"flex",flexDirection:"column",gap:12}}>
                <div style={{padding:"10px 14px",background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:10}}>
                  <p style={{fontSize:"0.8rem",fontWeight:600,color:"#1E3A8A",marginBottom:2}}>📧 Email Support</p>
                  <p style={{fontSize:"0.8rem",color:"#2563EB"}}>support@skypa.com</p>
                </div>
                <button style={{width:"100%",padding:"11px",background:"#2563EB",color:"#fff",border:"none",borderRadius:10,fontSize:"0.9rem",fontWeight:700,cursor:"pointer"}}
                  onClick={()=>{ alert("Our team will respond within 24 hours."); setShowSupport(false); }}>
                  Send Message
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

export default ClientManagerHome;
